import { Run } from 'utils/api'

/**
 * Gets the appropriate file extension for the export type
 * Embeddings are always TSV format, Images are always ZIP format
 */
const getFileExtension = (type: 'embeddings' | 'images'): string => {
  return type === 'embeddings' ? 'tsv' : 'zip'
}

/**
 * Generates a smart filename for downloads based on run details and export type
 */
export const generateFileName = (
  type: 'embeddings' | 'images',
  runs: Run[],
  runIdString?: string
): string => {
  const timestamp = new Date().toISOString().split('T')[0] // YYYY-MM-DD format
  const time = new Date().toTimeString().split(' ')[0].replace(/:/g, '') // HHMMSS format
  const extension = getFileExtension(type)
  
  if (runs.length === 1) {
    const run = runs[0]
    const runId = runIdString || run.id
    const instrumentId = run.instrument_serial_number || 'unknown'
    
    return `${type}_${runId}_${instrumentId}_${timestamp}_${time}.${extension}`
  }
  
  if (runs.length > 1) {
    const runIds = runs.map(r => r.id).join('-')
    return `${type}_multi_run_${runIds}_${timestamp}_${time}.${extension}`
  }
  
  // Fallback if no runs provided
  return `${type}_export_${timestamp}_${time}.${extension}`
}

/**
 * Generates a human-readable description for the download
 */
export const generateDownloadDescription = (
  type: 'embeddings' | 'images',
  runs: Run[],
  runIdString?: string
): string => {
  if (runs.length === 1) {
    const run = runs[0]
    const runId = runIdString || run.id
    const instrumentId = run.instrument_serial_number
    const startTime = run.start_time ? new Date(run.start_time).toLocaleDateString() : 'Unknown date'
    
    return `${type === 'embeddings' ? 'Embeddings' : 'Images'} from Run ${runId}${instrumentId ? ` (${instrumentId})` : ''} - ${startTime}`
  }
  
  if (runs.length > 1) {
    return `${type === 'embeddings' ? 'Embeddings' : 'Images'} from ${runs.length} runs`
  }
  
  return `${type === 'embeddings' ? 'Embeddings' : 'Images'} export`
}

/**
 * Validates and sanitizes a filename
 */
export const sanitizeFileName = (fileName: string): string => {
  // Split filename and extension
  const lastDotIndex = fileName.lastIndexOf('.')
  const hasExtension = lastDotIndex > 0
  const name = hasExtension ? fileName.substring(0, lastDotIndex) : fileName
  const extension = hasExtension ? fileName.substring(lastDotIndex) : ''
  
  // Sanitize the name part
  let sanitizedName = name
    .replace(/[<>:"/\\|?*]/g, '_') // Replace invalid characters with underscore
    .replace(/\s+/g, '_') // Replace spaces with underscore
    .replace(/_+/g, '_') // Replace multiple underscores with single
    .replace(/^_+/, '') // Remove leading underscores
    .replace(/_+$/, '') // Remove trailing underscores
  
  // Limit length (accounting for extension)
  const maxNameLength = 255 - extension.length
  if (sanitizedName.length > maxNameLength) {
    sanitizedName = sanitizedName.substring(0, maxNameLength)
    // Remove any trailing underscores after truncation
    sanitizedName = sanitizedName.replace(/_+$/, '')
  }
  
  return sanitizedName + extension
}

/**
 * Ensures the filename has the correct extension
 * Automatically adds .tsv for embeddings and .zip for images if missing
 */
export const ensureCorrectExtension = (
  fileName: string,
  type: 'embeddings' | 'images'
): string => {
  const extension = getFileExtension(type)
  const hasCorrectExtension = fileName.toLowerCase().endsWith(`.${extension}`)
  
  if (!hasCorrectExtension) {
    // Remove any existing extension and add the correct one
    const nameWithoutExtension = fileName.replace(/\.[^/.]+$/, '')
    return `${nameWithoutExtension}.${extension}`
  }
  
  // If it has the correct extension but wrong case, normalize it
  const nameWithoutExtension = fileName.replace(/\.[^/.]+$/, '')
  return `${nameWithoutExtension}.${extension}`
}
